import { useCallback } from "react";
import {
  approveBudget,
  rejectBudget,
  updateBudget,
  IApprovalPayload,
} from "../../../../api/fetchBudget";
import {
  BudgetRecipientEnum,
  IBudgetFormData,
  IBudgetService,
  IClientData,
  IVehicleData,
} from "../types";
import { buildBudgetApiPayload } from "../utils/buildBudgetApiPayload";
import { useNotification } from './useNotification';

interface IUseBudgetRoleActionsParams {
  budgetId: string;
  occurrenceId: string;
  initialData?: IBudgetFormData;
  rejectionObservation: string;
  files: Array<{
    file?: File;
    name: string;
    type?: string;
    size?: string | number;
  }>;
  recipient: string | undefined;
  clientData: IClientData | undefined;
  vehicleData: IVehicleData | undefined;
  services: IBudgetService[];
  profile?: BudgetRecipientEnum;
  uploadFiles: (
    filesToUpload: File[],
    budgetIdToUse?: string
  ) => Promise<boolean>;
  buildFormDataToSend: () => IBudgetFormData;
  onSave?: (data: IBudgetFormData) => void;
  username?: string;
}

export const useBudgetRoleActions = (params: IUseBudgetRoleActionsParams) => {
  const {
    budgetId,
    occurrenceId,
    initialData,
    rejectionObservation,
    files,
    recipient,
    clientData,
    vehicleData,
    services,
    profile,
    uploadFiles,
    buildFormDataToSend,
    onSave,
    username,
  } = params;

  const getRole = (): "factory" | "consultant" => {
    return profile === BudgetRecipientEnum.TOWER ? "factory" : "consultant";
  };

  const buildApiPayload = useCallback(() => {
    return buildBudgetApiPayload({
      occurrenceId,
      recipient,
      clientData,
      vehicleData,
      services,
      proposalServices: initialData?.proposalServices,
    });
  }, [
    occurrenceId,
    recipient,
    clientData,
    vehicleData,
    services,
    initialData?.proposalServices,
  ]);

  const handleReject = useCallback(async () => {
    if (!initialData?.versionNumber || !budgetId) {
      alert("Erro: dados do orçamento não encontrados");
      return;
    }

    const apiPayload = buildApiPayload();
    const role = getRole();

    const result = await rejectBudget({
      budgetId,
      versionNumber: initialData.versionNumber,
      role,
      rejectionObservation: rejectionObservation || "",
      services: apiPayload.services,
      proposalServices: apiPayload.proposalServices,
      subtotal: apiPayload.subtotal,
      discount: apiPayload.discount,
      netTotal: apiPayload.netTotal,
      counterProposalObservation: initialData?.counterProposalObs,
      counterProposalValue: initialData?.counterProposalValue,
    });

    if (result.error) {
      alert(`Erro ao recusar orçamento: ${result.error}`);
      return;
    }

    const filesToUpload: File[] = files
      .filter((fileItem) => fileItem.file !== undefined)
      .map((fileItem) => fileItem.file as File);

    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload, budgetId);
    }

    if (onSave) {
      const formDataToSend = buildFormDataToSend();
      onSave(formDataToSend);
    }
  }, [
    budgetId,
    initialData,
    rejectionObservation,
    files,
    buildApiPayload,
    uploadFiles,
    buildFormDataToSend,
    onSave,
    username,
  ]);

  const handleApprove = useCallback(async () => {
    if (!initialData?.versionNumber || !budgetId) {
      alert("Erro: dados do orçamento não encontrados");
      return;
    }

    const apiPayload = buildApiPayload();
    const role = getRole();

    const approvalPayload: IApprovalPayload = {
      services: apiPayload.services,
      proposalServices: apiPayload.proposalServices,
      subtotal: apiPayload.subtotal,
      discount: apiPayload.discount,
      netTotal: apiPayload.netTotal,
      observation: initialData?.observation || undefined,
      counterProposalObservation: initialData?.counterProposalObs,
      counterProposalValue: initialData?.counterProposalValue,
    };

    const result = await approveBudget(
      budgetId,
      initialData.versionNumber,
      role,
      approvalPayload
    );

    if (result.error) {
      alert(`Erro ao aprovar orçamento: ${result.error}`);
      return;
    }

    // Upload de arquivos se houver
    const filesToUpload: File[] = files
      .filter((fileItem) => fileItem.file !== undefined)
      .map((fileItem) => fileItem.file as File);

    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload, budgetId);
    }

    if (onSave) {
      const formDataToSend = buildFormDataToSend();
      onSave(formDataToSend);
    }
  }, [
    budgetId,
    initialData,
    files,
    buildApiPayload,
    uploadFiles,
    buildFormDataToSend,
    onSave,
    username,
  ]);

  const handleUpdate = useCallback(async () => {
    if (!initialData?.versionNumber || !budgetId) {
      alert("Erro: dados do orçamento não encontrados");
      return;
    }

    const apiPayload = buildApiPayload();
    const role = getRole();

    const updatePayload: IApprovalPayload = {
      services: apiPayload.services,
      proposalServices: apiPayload.proposalServices,
      subtotal: apiPayload.subtotal,
      discount: apiPayload.discount,
      netTotal: apiPayload.netTotal,
      observation: initialData?.observation || undefined,
      counterProposalObservation: initialData?.counterProposalObs,
      counterProposalValue: initialData?.counterProposalValue,
    };

    const result = await updateBudget(
      budgetId,
      initialData.versionNumber,
      role,
      updatePayload
    );

    if (result.error) {
      alert(`Erro ao atualizar orçamento: ${result.error}`);
      return;
    }

    // Upload de arquivos se houver
    const filesToUpload: File[] = files
      .filter((fileItem) => fileItem.file !== undefined)
      .map((fileItem) => fileItem.file as File);

    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload, budgetId);
    }

    if (onSave) {
      const formDataToSend = buildFormDataToSend();
      onSave(formDataToSend);
    }
  }, [
    budgetId,
    initialData,
    files,
    buildApiPayload,
    uploadFiles,
    buildFormDataToSend,
    onSave,
    username,
  ]);

  return {
    handleReject,
    handleApprove,
    handleUpdate,
  };
};
